import React, { createRef, useCallback, useEffect, useState, isValidElement, useMemo } from 'react';
import Select, { type InputActionMeta, type SelectComponentsConfig } from '@atlaskit/select';
import type { GroupHeadingProps } from '@atlaskit/react-select';
import sendExperienceAnalytics from '@atlassian/jira-common-experience-tracking-analytics/src/index.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import {
	type SelectValue,
	type BaseSelectProps as Props,
	MetaActions,
	type SelectOption,
} from '../../../common/types.tsx';
import { isSingleValueOption } from '../../../common/utils/is-single-value-option.tsx';
import Control from './control/index.tsx';
import DropdownIndicator from './dropdown-indicator/index.tsx';
import Menu from './menu/index.tsx';
import messages from './messages.tsx';
import DefaultNoOptionsMessage from './no-options-message/index.tsx';
import Option from './option/index.tsx';
import { GroupHeading } from './group-heading/index.tsx';

export const selectComponents = {
	Control,
	DropdownIndicator,
	LoadingIndicator: () => null,
	Menu,
	Option,
} as const;

export const singleSelectComponents: SelectComponentsConfig<SelectOption, true> = {
	Control,
	DropdownIndicator,
	LoadingIndicator: undefined,
	Menu,
} as const;

const onIsOptionDisabled = (option: SelectOption, value: SelectValue): boolean => {
	if (option.optionType === 'error') {
		return true;
	}
	// if an option is only unselectable and currently selected, it should not be a disabled option
	if ('isOnlyUnselectable' in option && option.isOnlyUnselectable) {
		const isSelectedOption =
			isSingleValueOption(option) &&
			value.map((v) => isSingleValueOption(v) && v.value).includes(option.value);
		return !(isSelectedOption && option.isOnlyUnselectable);
	}
	if ('isDisabled' in option && option.isDisabled) {
		return option.isDisabled;
	}
	return false;
};

// Needed to disable filtering from react-select
const noFilterOptions = () => true;

export const BaseSelect = ({
	onReload,
	placeholder,
	NoOptionsMessage = DefaultNoOptionsMessage,
	isMulti = true,
	setInitialFocusRef,
	totalCount,
	components: componentsSelect,
	options,
	isInBetweenColumnPickerEnabled,
	...restProps
}: Props) => {
	const {
		onInputChange: onInputChangeProp,
		inputValue: inputValueProp,
		id,
		menuListProps: {
			isLoading,
			error: { isError, error },
		},
		experienceAnalyticsAttributes,
		value,
	} = restProps;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const selectRef = createRef<any>(); // use any type for react-select ref
	const [inputValue, setInputValue] = useState<string | undefined>(inputValueProp ?? '');
	const { formatMessage } = useIntl();

	useEffect(() => {
		// only set once when the component mounted
		if (selectRef.current && setInitialFocusRef) {
			setInitialFocusRef(selectRef.current.select.inputRef);
		}
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (options && !(isLoading ?? false)) {
			let errorType = 'unknown';

			if (error != null && isClientFetchError(error)) {
				errorType = 'networkError';
			}

			const errorAttributes = error
				? {
						errorType,
						message: error.message,
					}
				: null;

			sendExperienceAnalytics({
				analyticsSource: 'refinement-bar-select',
				wasExperienceSuccesful: error == null,
				experience: 'showOptions',
				application: null,
				edition: null,
				additionalAttributes: {
					filter: id ?? 'unknown',
					...experienceAnalyticsAttributes,
					...errorAttributes,
				},
			});
		}
	}, [isError, error, isLoading, options, id, experienceAnalyticsAttributes]);

	const hasEnabledOption = useMemo(
		() => options?.some((option) => !onIsOptionDisabled(option, value)),
		[options, value],
	);
	const onInputChange = useCallback(
		(newInputValue: string, meta: InputActionMeta) => {
			/**
			 * Do not clear the filter input value when an input is selected or blured.
			 * NOTE: We are also returning when 'menu-close' event are triggered,
			 * which happens when the input is clicked for some reason, but not when we click outside of the dropdown
			 * */
			if (
				Array.from<string>([
					MetaActions.setValue,
					MetaActions.inputBlur,
					MetaActions.menuClose,
				]).includes(meta.action) &&
				!newInputValue.length
			) {
				return;
			}

			// Reload defaultOptions in case of potential error by changing key on AsyncSelect
			if (meta.action === MetaActions.inputChange && !newInputValue.length) {
				onReload?.('');
			}

			onInputChangeProp && onInputChangeProp(newInputValue, meta);
			setInputValue(newInputValue);
		},
		[onInputChangeProp, onReload],
	);

	useEffect(() => {
		setInputValue(inputValueProp);
	}, [inputValueProp]);

	const getOptionLabel = useCallback((option: SelectOption) => {
		if (isSingleValueOption(option)) {
			if (typeof option.secondaryLabel === 'string') {
				return `${option.label}, ${option.secondaryLabel}`;
			}
			if (
				// remove !isValidElement(data.secondaryLabel) with cleanup with m7.3-mm2-custom-fields-with-existing-uis_vzhn7
				typeof option.secondaryLabel === 'object' &&
				!isValidElement(option.secondaryLabel)
			) {
				return `${option.label}, ${option.secondaryLabel.label}`;
			}
			return option.label;
		}
		return option.optionType === 'group' ? option?.label ?? '' : '';
	}, []);

	// placeholder type is reactNode and aria-label won't allow, for safe side checking the type and empty value
	const ariaLabel =
		typeof placeholder === 'string' && placeholder
			? placeholder
			: formatMessage(messages.searchPlaceholder);

	const OriginalOptionComponent = componentsSelect?.Option || undefined;

	// Note: this is a workaround to not visually focus any option, when there are only disabled options in the list
	// react-select autofocuses the first option, see more here: https://github.com/JedWatson/react-select/pull/4080
	// tracked bug ticket: https://product-fabric.atlassian.net/browse/DSP-17138
	const OptionComponentExtended = useCallback(
		(props: { isFocused: boolean }) =>
			OriginalOptionComponent ? (
				<OriginalOptionComponent
					{...props}
					isFocused={hasEnabledOption ? props.isFocused : false}
				/>
			) : (
				<></>
			),
		[OriginalOptionComponent, hasEnabledOption],
	);

	const unselectedOptions: SelectOption[] | undefined = useMemo(() => {
		if (!isInBetweenColumnPickerEnabled) {
			return [];
		}

		if (!inputValue) {
			return options?.filter((option) => option.optionType === 'group') || [];
		}

		const selectedOptions = new Set(value.map((v) => (isSingleValueOption(v) ? v.value : '')));
		return (
			options?.filter(
				(option) => isSingleValueOption(option) && !selectedOptions.has(option.value),
			) || []
		);
	}, [isInBetweenColumnPickerEnabled, inputValue, options, value]);

	const memoizedComponents = useMemo(() => {
		const totalCountOfUnselectedFields = totalCount ? totalCount - value.length : null;
		return {
			...componentsSelect,
			...(fg('jira_inline_field_config_gate')
				? {
						GroupHeading: (props: GroupHeadingProps<SelectOption>) => (
							<GroupHeading {...props} totalCount={totalCountOfUnselectedFields} />
						),
					}
				: {}),
			...(OriginalOptionComponent !== undefined ? { Option: OptionComponentExtended } : {}),
		};
	}, [
		componentsSelect,
		OriginalOptionComponent,
		OptionComponentExtended,
		totalCount,
		value.length,
	]);

	return (
		<Select
			{...restProps}
			options={
				isInBetweenColumnPickerEnabled && fg('jira_inline_field_config_gate')
					? unselectedOptions
					: options
			}
			// Using classNamePrefix to identify the select component in cypress test, testId doesn't work (doc https://atlassian.design/components/select/testing)
			classNamePrefix="jql-builder-basic-picker-atlaskit-select"
			components={memoizedComponents}
			cacheOptions={false}
			ref={selectRef}
			autoFocus={!setInitialFocusRef}
			backspaceRemovesValue={false}
			blurInputOnSelect={false}
			closeMenuOnSelect={false}
			controlShouldRenderValue={false}
			hideSelectedOptions={false}
			isClearable={false}
			isMulti={isMulti}
			inputValue={inputValue}
			menuIsOpen
			menuShouldScrollIntoView={false}
			tabSelectsValue={false}
			onInputChange={onInputChange}
			isOptionDisabled={onIsOptionDisabled}
			reload={onReload}
			filterOption={noFilterOptions}
			placeholder={placeholder ?? formatMessage(messages.searchPlaceholder)}
			label={ariaLabel}
			noOptionsMessage={NoOptionsMessage}
			getOptionLabel={getOptionLabel}
		/>
	);
};
